import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlowProvider,
  ReactFlow,
  Controls,
  Background,
  addEdge,
  useReactFlow,
  MarkerType,
  useOnSelectionChange,
} from '@xyflow/react';

import CustomNodePath from './CustomNodes/CustomNode';
import CustomNodeEmpty from './CustomNodes/CustomNodeEmpty';

import '@xyflow/react/dist/style.css';
import './xy-themas.css';
import { newUid } from '@/util';

import { axios } from '@/axios';
import { config } from '@/config';
import {
  cleanConection,
  filterIdFromArray,
} from '@/components/LearningPath/util';

import { DnDProvider, useDnD } from './DnDContext';
import { useDrop } from 'react-dnd';

const nodeTypes = {
  customNode: CustomNodePath,
  customNodeEmpty: CustomNodeEmpty,
};

const getId = () => newUid(10);
const nodeOrigin = [0.5, 0];

const FlowComponent = ({
  learningpathId,
  setNodeSelected,
  onNodesChange,
  setNodes,
  nodes,
  onEdgesChange,
  setEdges,
  edges,
  handleChangeNode,
  handleRemoveNode,
  setIsDraggingDisabled,
  isDraggingDisabled,
  handleRemoveChildNode,
  setMisingConnect,
}) => {
  const [xPosition, setXPosition] = useState(0);
  const { screenToFlowPosition } = useReactFlow();
  const [prevSelect, setPrevSelect] = useState(null);

  const rf = useReactFlow();

  const [type, setType] = useDnD();

  const flowRef = useRef(null);

  useEffect(() => {
    if (flowRef.current) {
      const blockWidth = flowRef.current.getBoundingClientRect().width;
      setXPosition(blockWidth / 2);
    }
  }, []);

  const onConnect = useCallback(
    (params) => {
      if (params?.source === params?.target) return;
      let isAnimated;
      setNodes((nds) =>
        nds.map((el) => {
          if (el?.id == params?.target) {
            if (el?.data?.mandatory === false) {
              isAnimated = true;
            }
          }
          return el;
        })
      );
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            markerEnd: { type: MarkerType.Arrow },
            animated: isAnimated && isAnimated,
          },
          eds
        )
      );

      const nodeObject = nodes?.find((nds) => nds?.id === params?.target);
      const newEds = edges
        ?.filter((el) => el?.target === params?.target)
        ?.map((el) => el?.source)
        ?.concat(params?.source);

      const newEdsConect = edges
        ?.filter((el) => el?.target === params?.target)
        ?.map(
          (el) =>
            el?.sourceHandle + '/' + (el?.targetHandle ? el?.targetHandle : '')
        )
        ?.concat(
          params?.sourceHandle +
            '/' +
            (params?.targetHandle ? params?.targetHandle : '')
        );
      if (nodeObject?.id) {
        handleChangeNode(nodeObject?.id, {
          roots: newEds,
          rootDescriptions: newEdsConect,
        });
      }
    },
    [setEdges, nodes, edges]
  );

  const onConnectEnd = useCallback(
    (event, connectionState) => {
      if (!connectionState.isValid && connectionState?.fromPosition !== 'top') {
        const id = getId();
        let sourceHandle;

        const { clientX, clientY } =
          'changedTouches' in event ? event.changedTouches[0] : event;

        const flowPos = screenToFlowPosition({ x: clientX, y: clientY });
        const offsetY = 0;
        const position = { x: flowPos.x, y: flowPos.y - offsetY };

        if (connectionState.fromPosition === 'left') {
          position.x = position.x - 200;
          position.y = position.y - 50;
          sourceHandle = 'a';
        } else if (connectionState.fromPosition === 'top') {
          sourceHandle = 'b';
        } else if (connectionState.fromPosition === 'right') {
          position.x = position.x + 200;
          position.y = position.y - 50;
          sourceHandle = 'c';
        } else if (connectionState.fromPosition === 'bottom') {
          sourceHandle = 'd';
        }

        const newNode = {
          id,
          type: 'customNodeEmpty',
          position: {
            x: position.x,
            y: position.y,
          },
          data: {
            handlePosition: connectionState.fromPosition,
            empty: true,
            handleRemoveNode: handleRemoveNode,
            mandatory:
              connectionState?.fromNode?.data?.type !== 'homepage'
                ? connectionState?.fromNode?.data?.mandatory
                : true,
          },
          origin: [0.5, 0.0],
        };

        setNodes((nds) => nds.concat(newNode));
        setEdges((eds) =>
          eds.concat({
            type: 'smoothstep',
            id,
            animated:
              !connectionState?.fromNode?.data?.mandatory &&
              connectionState?.fromNode?.data?.type !== 'homepage',
            source: connectionState.fromNode.id,
            targetHandle:
              sourceHandle === 'c' ? 'a-1' : sourceHandle === 'a' ? 'c-1' : 'b',
            sourceHandle: sourceHandle,
            target: id,
            markerEnd: {
              type: MarkerType.Arrow,
            },
          })
        );
      }
    },
    [screenToFlowPosition]
  );

  const onChange = useCallback(
    ({ nodes, edges }) => {
      const selectNode = nodes.map((node) => node.id);
      const checkNode = filterIdFromArray(selectNode, prevSelect);

      if (selectNode?.length > 0) {
        const selectedNode = nodes?.find((el) => el?.id === checkNode[0]);
        setPrevSelect(selectedNode);
        setNodeSelected(selectedNode);
      } else {
        setNodeSelected(null);
        setPrevSelect(null);
      }
    },
    [setNodeSelected, setPrevSelect, prevSelect]
  );

  useOnSelectionChange({
    onChange,
  });

  const onNodeDragStop = useCallback((_, node, nodes) => {
    if (node?.type !== 'customNodeEmpty') {
      nodes?.map(
        (nds) =>
          nds?.type === 'customNode' &&
          handleChangeNode(nds?.id, {
            x: nds?.position?.x,
            y: nds?.position?.y,
          })
      );
    } else {
      setNodeSelected(node);
    }
  }, []);

  const handleOnDelete = (type, id, nodes, edges, cleanList) => {
    if (type === 'edge') {
      const updateNodeConect = cleanConection(
        type,
        id,
        nodes,
        edges,
        cleanList
      );

      if (updateNodeConect && updateNodeConect?.node?.type === 'customNode') {
        handleChangeNode(updateNodeConect?.node?.id, {
          roots: updateNodeConect?.rootsId,
          rootDescriptions: updateNodeConect?.rootsConect,
        });
      }
    }
    if (type === 'node') {
      axios.delete(
        `${config.backend}/learningpathnode/${learningpathId}/${id?.id}`
      );
    }
  };

  const onDelete = useCallback(
    (dlItems) => {
      if (dlItems?.edges?.length > 0) {
        const cleanList = edges.filter(
          (item1) => !dlItems?.edges.some((item2) => item2.id === item1.id)
        );

        dlItems?.edges?.map(
          (eds) =>
            eds?.id && handleOnDelete('edge', eds?.id, nodes, edges, cleanList)
        );
      }

      if (dlItems?.nodes?.length > 0) {
        dlItems?.nodes?.map(
          (nds) =>
            nds?.type === 'customNode' &&
            handleOnDelete('node', nds, nodes, edges)
        );
      }
    },
    [nodes, edges]
  );

  const onPaneClick = () => {
    setIsDraggingDisabled(false);
    setNodeSelected(null);
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();
      if (!type) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = await axios.post(`${config.backend}/learningpathnodes`, {
        learningpathId: learningpathId,
        objectId: type?.objectId,
        type: type?.type,
        subType: type?.subType,
        x: position?.x,
        y: position?.y,
        roots: [],
        rootDescriptions: [],
        mandatory: type?.mandatory,
        blocking: type?.blocking,
        notCompleted: type?.notCompleted,
        notPassed: type?.notPassed,
      });

      const createNodeId = newNode?.data?.id;

      const newNodeAdd = {
        id: `${createNodeId}`,
        type: 'customNode',
        position,
        data: {
          learningPathId: learningpathId,
          nodeID: type?.objectId,
          trainingId: type?.type === 'training' && type?.objectId,
          courseId: type?.type === 'course' && type?.objectId,
          title: type?.name,
          description: '',
          type: type?.type,
          courseType: type?.subType,
          mandatory: type?.mandatory,
          blocking: type?.blocking,
          notCompleted: type?.notCompleted,
          notPassed: type?.notPassed,
        },
      };

      const moveDataNode = nodes
        ?.find((nds) => nds?.data?.nodeID === type?.learningpathId)
        ?.data?.child?.find((el) => el.id === type?.id);

      handleRemoveChildNode(
        {
          id: type?.id,
          data: {
            learningPathId: type?.learningpathId,
            roots: moveDataNode?.roots,
            rootDescriptions: moveDataNode?.rootDescriptions,
          },
        },
        nodes
      );

      // console.log(nodes);

      setNodes((nds) => nds.concat(newNodeAdd));
      setType(null);
    },
    [screenToFlowPosition, type]
  );

  const [{ isOver }, containerRef] = useDrop({
    accept: 'card',
    drop: (item) => {
      // console.log(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const onNodesChangeWithHistory = useCallback(
    (changes) => {
      setMisingConnect(null);
      onNodesChange(changes);
    },
    [onNodesChange]
  );

  const onEdgesChangeWithHistory = useCallback(
    (changes) => {
      setMisingConnect(null);
      onEdgesChange(changes);
    },
    [onEdgesChange]
  );

  return (
    <div ref={containerRef} className="dndflow edit flex h-full w-full">
      <div
        ref={flowRef}
        className="reactflow-wrapper h-full w-full bg-grey-01"
        style={{ width: '100%', height: '100%' }}
      >
        {nodes && edges && xPosition !== 0 && (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChangeWithHistory}
            onEdgesChange={onEdgesChangeWithHistory}
            onNodeDragStop={onNodeDragStop}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onConnect={onConnect}
            isValidConnection={(connection) =>
              connection?.source !== connection?.target
            }
            // fitView
            nodeTypes={nodeTypes}
            onConnectEnd={onConnectEnd}
            deleteKeyCode={['Delete', 'Backspace']}
            onDelete={onDelete}
            panOnDrag={!isDraggingDisabled}
            onPaneClick={onPaneClick}
            nodeOrigin={nodeOrigin}
            defaultViewport={{ x: xPosition, y: 50, zoom: 0.8 }}
          >
            <Controls />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        )}
      </div>
    </div>
  );
};

function XyflowComponent({
  learningpathId,
  setNodeSelected,
  onNodesChange,
  setNodes,
  nodes,
  onEdgesChange,
  setEdges,
  edges,
  handleChangeNode,
  handleRemoveNode,
  setIsDraggingDisabled,
  isDraggingDisabled,
  handleRemoveChildNode,
  setMisingConnect,
}) {
  return (
    <ReactFlowProvider>
      <DnDProvider>
        <FlowComponent
          learningpathId={learningpathId}
          setNodeSelected={setNodeSelected}
          onNodesChange={onNodesChange}
          setNodes={setNodes}
          nodes={nodes}
          onEdgesChange={onEdgesChange}
          setEdges={setEdges}
          edges={edges}
          handleChangeNode={handleChangeNode}
          handleRemoveNode={handleRemoveNode}
          setIsDraggingDisabled={setIsDraggingDisabled}
          isDraggingDisabled={isDraggingDisabled}
          handleRemoveChildNode={handleRemoveChildNode}
          setMisingConnect={setMisingConnect}
        />
      </DnDProvider>
    </ReactFlowProvider>
  );
}
export default XyflowComponent;
